import type { UseQueryOptions } from '@tanstack/react-query';

import { settingsListSettings } from '@crossnokaye/typescript-sdk/funcs/settingsListSettings';
import { ControlledDeviceSetting } from '@crossnokaye/typescript-sdk/models/components';
import { SDKError } from '@crossnokaye/typescript-sdk/models/errors';
import {
  ListSettingsRequest,
  ListSettingsResponse,
} from '@crossnokaye/typescript-sdk/models/operations';

import { AtlasClientFunction } from '@/client/types';
import { AtlasQueryWithOrgIdAndAgentId } from '@/queries/types';

const transformListSettingsResponse = (
  response: ListSettingsResponse
): Array<ControlledDeviceSetting> | undefined => {
  const responseBody = response.listSettingsResponseBody;
  if (!responseBody) {
    return undefined;
  }
  return responseBody.values || undefined;
};

export type ListSettingsQueryProps = AtlasQueryWithOrgIdAndAgentId &
  ListSettingsRequest & {
    transform?: (response: ListSettingsResponse) => Array<ControlledDeviceSetting> | undefined;
    client: AtlasClientFunction;
  };

export const listSettingsQuery = ({
  orgId,
  agentId,
  deploymentId,
  aliases,
  deviceIds,
  deviceKinds,
  settingIds,
  transform = transformListSettingsResponse,
  client,
}: ListSettingsQueryProps): UseQueryOptions<
  ListSettingsResponse,
  SDKError,
  Array<ControlledDeviceSetting> | undefined
> => {
  return {
    enabled: !!orgId && !!agentId,
    queryKey: [
      orgId,
      agentId,
      'listSettings',
      deploymentId,
      aliases,
      deviceIds,
      deviceKinds,
      settingIds,
    ],
    queryFn: () =>
      client<ListSettingsRequest, ListSettingsResponse>({
        atlasFunc: settingsListSettings,
        request: {
          orgId: orgId!,
          agentId: agentId!,
          deploymentId,
          aliases,
          deviceIds,
          deviceKinds,
          settingIds,
        },
      }),
    select: transform,
    staleTime: Infinity,
  };
};
